// <product-info> (@theme/product): wybór wariantu, formularz i ilość, stos kolumny zakupu, pasek sticky ATC z kotwicami,
// odliczanie wysyłki, zakładki i „ostatnio oglądane”. Ten sam element działa na PDP (main-product)
// i w oknie szybkiego dodania (quick-add). Kontrakty: ARCHITEKTURA §5.2, §5.4, §5.5, §17.2.
import {
  Theme, EVENTS, emit, mq, debounce, onIdle, reducedMotion, cart, burst, announce, strings,
  fetchSection, swapFragments, formatMoney, standardEvent,
} from '@theme/core';

const DAY = 86_400_000;
const STACK_EDGE = 16;
const HOLIDAYS = ['1-1', '1-6', '5-1', '5-3', '8-15', '11-1', '11-11', '12-24', '12-25', '12-26'];
const warsaw = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Warsaw', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', hourCycle: 'h23',
});

// ----- Dni robocze i odliczanie wysyłki (FR-13-18, NT-16); daty jako północ UTC -----

// Niedziela Wielkanocna, algorytm Meeusa/Jonesa/Butchera.
export function easterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const h = (19 * a + b - Math.floor(b / 4) - Math.floor((b - Math.floor((b + 8) / 25) + 1) / 3) + 15) % 30;
  const l = (32 + 2 * (b % 4) + 2 * Math.floor(c / 4) - h - (c % 4)) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const n = h + l - 7 * m + 114;
  return Date.UTC(year, Math.floor(n / 31) - 1, (n % 31) + 1);
}

function isWorkday(day) {
  const weekday = day.getUTCDay();
  if (weekday === 0 || weekday === 6 || HOLIDAYS.includes(`${day.getUTCMonth() + 1}-${day.getUTCDate()}`)) return false;
  // Poniedziałek Wielkanocny i Boże Ciało; pozostałe święta ruchome wypadają w niedzielę.
  const easter = easterSunday(day.getUTCFullYear());
  return day.getTime() !== easter + DAY && day.getTime() !== easter + 60 * DAY;
}

function nextWorkday(day) {
  let next = new Date(day.getTime() + DAY);
  while (!isWorkday(next)) next = new Date(next.getTime() + DAY);
  return next;
}

// Godzina graniczna dotyczy magazynu, więc „dziś” i godzinę liczymy w Warszawie, nie w strefie klienta.
export function shippingPlan(now, cutoffHour) {
  const part = Object.fromEntries(warsaw.formatToParts(now).map(({ type, value }) => [type, Number(value)]));
  const today = new Date(Date.UTC(part.year, part.month - 1, part.day));
  const left = cutoffHour * 60 - (part.hour * 60 + part.minute);
  const shipsToday = left > 0 && isWorkday(today);
  const shipDay = shipsToday ? today : nextWorkday(today);
  return { minutesLeft: shipsToday ? left : 0, shipDay, deliveryDay: nextWorkday(shipDay), tomorrow: new Date(today.getTime() + DAY) };
}

const fill = (text = '', values = {}) => text.replace(/\{(\w+)\}/g, (match, key) => values[key] ?? match);

// Teksty z data-strings bloku dostępności (snippet availability).
export function shippingText(plan, s) {
  let ship;
  if (plan.minutesLeft > 0) {
    const h = Math.floor(plan.minutesLeft / 60);
    const m = plan.minutesLeft % 60;
    ship = fill(s.today, { time: h > 0 ? fill(s.hours, { h, m }) : fill(s.minutes, { m: Math.max(m, 1) }) });
  } else if (plan.shipDay.getTime() === plan.tomorrow.getTime()) {
    ship = s.tomorrow;
  } else {
    ship = fill(s.day, { day: s.weekdaysOn?.[plan.shipDay.getUTCDay()] });
  }
  const day = plan.deliveryDay;
  const two = (n) => String(n).padStart(2, '0');
  const date = `${s.weekdaysShort?.[day.getUTCDay()]} ${two(day.getUTCDate())}.${two(day.getUTCMonth() + 1)}`;
  const when = day.getTime() === plan.tomorrow.getTime() ? fill(s.deliveryTomorrow, { date }) : date;
  return { ship, delivery: fill(s.delivery, { date: when }) };
}

// ----- Pozostałe czyste funkcje -----

// Kontrakt z „Ostatnio oglądanymi” (G2): JSON tablica stringów ID, najnowszy pierwszy, maks. 12.
export function rememberViewed(id, storage) {
  try {
    storage ??= localStorage;
    const saved = JSON.parse(storage.getItem('viewedProducts') || '[]');
    const rest = (Array.isArray(saved) ? saved : []).map(String).filter((item) => item && item !== String(id));
    storage.setItem('viewedProducts', JSON.stringify([String(id), ...rest].slice(0, 12)));
  } catch {
    // Pamięć niedostępna (tryb prywatny, blokada) — lista po prostu się nie zapisze.
  }
}

// Ilość w granicach [min, max] i na siatce kroku liczonej od minimum (tak waliduje input number).
export function fitQuantity(value, { min = 1, max = Infinity, step = 1 }) {
  const clamped = Math.min(Math.max(Number(value) || min, min), Math.max(min, max));
  return min + Math.floor((clamped - min) / step) * step;
}

// Ta sama reguła co w snippecie buy-buttons (FR-13-21).
export function buttonLabel(variant, s) {
  if (variant?.available) return s.addToCart;
  const stock = variant?.inventory_quantity ?? 0;
  const tracked = variant?.inventory_management === 'shopify' && variant.inventory_policy === 'deny';
  return tracked && (stock <= 0 || (variant.quantity_rule?.min || 1) > stock) ? s.soldOut : s.unavailable;
}

const readJson = (el, fallback = null) => {
  try {
    return el ? JSON.parse(el.textContent) : fallback;
  } catch {
    return fallback;
  }
};

// Linia przez ekran na wysokości y: obserwator z takim marginesem widzi tylko przecięcie tej linii.
const lineMargin = (y) => `${-Math.round(y)}px 0px ${-Math.max(0, Math.round(innerHeight - y - 1))}px 0px`;
const headerStuck = () => parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height-stuck')) || 0;

class ProductInfo extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.pdp = this.dataset.context === 'pdp';
    // Fragmenty (np. galeria w oknie szybkiego dodania) mogą leżeć obok elementu.
    this.root = this.closest('dialog, .shopify-section') || this;
    this.variants = readJson(this.querySelector('[data-variants]'), []);
    this.variant = this.findVariant(this.selectedOptions());
    this.available = !this.querySelector('[data-add-button]')?.disabled;
    this.inCart = new Map(this.variants.map((variant) => [String(variant.id), variant.in_cart || 0]));
    const id = this.currentId();
    if (id && !this.inCart.has(id)) this.inCart.set(id, Number(this.querySelector('[data-cart-quantity]')?.dataset.cartQuantity) || 0);
    this.queue = [];

    const on = (target, type, listener) => target.addEventListener(type, listener, { signal });
    on(this, 'change', (event) => this.onChange(event));
    on(this, 'click', (event) => this.onClick(event));
    on(this, 'keydown', (event) => this.onKeydown(event));
    on(this, 'submit', (event) => this.onSubmit(event));
    on(this, 'input', ({ target }) => target.matches('.quantity__input') && this.syncQuantity());
    on(this, 'focusin', ({ target }) => target.matches('.quantity__input') && target.select());
    on(this, 'focusout', ({ target }) => target.matches('.quantity__input') && this.fixQuantity(target));
    on(document, EVENTS.cartChange, (event) => this.onCartChange(event.detail));
    this.syncQuantity();
    // Przeglądarka mogła odtworzyć zaznaczenie radia (Wstecz, przeładowanie) inne niż wariant z renderu.
    if (this.variant && String(this.variant.id) !== id) this.selectVariant();

    if (!this.pdp) return this.sendView();
    rememberViewed(this.dataset.productId);
    this.startShipping();
    this.setupStack(signal);
    this.setupStickyBar(signal);
    this.setupReviews(signal);
    this.watchView();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.request?.abort();
    this.request = null;
    clearInterval(this.shippingTimer);
    clearTimeout(this.errorTimer);
    cancelAnimationFrame(this.stackFrame);
    for (const observer of [this.stackSizes, this.stuckObserver, this.coverObserver, this.barObserver, this.footerObserver, this.barSize, this.spyObserver, this.anchorWatch, this.viewObserver]) {
      observer?.disconnect();
    }
  }

  get quantityInput() {
    return this.querySelector('.quantity__input');
  }

  get pending() {
    return Boolean(this.request);
  }

  currentId() {
    return this.querySelector('[data-variant-input]')?.value;
  }

  onChange({ target }) {
    if (target.closest('[data-variant-picker]')) this.selectVariant(true);
    else if (target.matches('.product-tabs__radio')) this.syncReviews();
  }

  onClick(event) {
    const link = event.target.closest('[data-anchor]');
    if (link && this.anchorTargets?.has(link)) {
      event.preventDefault();
      return this.scrollToSection(this.anchorTargets.get(link));
    }
    const target = event.target.closest('button, label');
    if (!target) return;
    if (target.matches('.quantity__button')) this.stepQuantity(target.name === 'plus' ? 1 : -1);
    else if (target.matches('[data-sticky-add]')) this.addToCart(target);
    else if (this.isSpecsLink(target)) this.openSpecs(event);
  }

  // „Wszystkie dane techniczne →” to label (F3 daje mu tabindex="0"), więc Enter i Spacja działają jak klik.
  onKeydown(event) {
    if ((event.key === 'Enter' || event.key === ' ') && this.isSpecsLink(event.target)) this.openSpecs(event);
  }

  isSpecsLink(el) {
    return el.matches('label') && Boolean(el.htmlFor) && el.htmlFor === this.querySelector('[data-specs-tab]')?.id && !el.closest('[data-tabs-nav]');
  }

  onSubmit(event) {
    if (!event.target.matches('form[data-product-form]')) return;
    event.preventDefault();
    this.addToCart(event.submitter || event.target.querySelector('[data-add-button]'));
  }

  // ----- Wariant (PDP-VAR-05…08, §5.5) -----

  selectedOptions() {
    return [...this.querySelectorAll('[data-variant-picker] [data-option]')].map((option) => ({
      name: option.dataset.optionName,
      value: option.matches('fieldset') ? option.querySelector('input:checked')?.value ?? '' : option.dataset.value,
    }));
  }

  findVariant(selected) {
    return this.variants.find((variant) => variant.options.every((value, i) => value === selected[i]?.value)) ?? null;
  }

  // spoken: wybór użytkownika — po odświeżeniu fragmentów nowy stan trafia do #LiveRegion (FR-13-32).
  selectVariant(spoken = false) {
    const selected = this.selectedOptions();
    this.markOptions(selected);
    this.applyVariant(this.findVariant(selected), selected, spoken);
  }

  // Wartość niedostępna w połączeniu z pozostałymi wybranymi opcjami: przekreślona, nadal klikalna.
  markOptions(selected) {
    const { soldOutValue = '' } = strings(this.querySelector('[data-variant-picker]'));
    this.querySelectorAll('[data-variant-picker] [data-option]').forEach((option, index) => {
      for (const input of option.querySelectorAll('input')) {
        const values = selected.map((item, i) => (i === index ? input.value : item.value));
        const available = this.variants.some((variant) => variant.available && variant.options.every((value, i) => value === values[i]));
        input.closest('label')?.querySelector('.variant-picker__card')?.classList.toggle('is-unavailable', !available);
        if (available) {
          input.removeAttribute('aria-disabled');
          input.removeAttribute('aria-label');
        } else {
          input.setAttribute('aria-disabled', 'true');
          input.setAttribute('aria-label', fill(soldOutValue, { value: input.value }));
        }
      }
    });
  }

  // Najpierw natychmiast z JSON wariantów (przycisk, cena, adres, slajd), potem fragmenty z sekcji SRA.
  async applyVariant(variant, selected, spoken) {
    this.variant = variant;
    this.renderState();
    if (variant) this.renderPrice(variant);
    this.updateLinks(variant);
    if (variant?.featured_media_id) this.root.querySelector('media-gallery')?.showMedia?.(variant.featured_media_id, { animate: false });
    const select = this.selectEvent(selected);
    this.request?.abort();
    const request = (this.request = variant ? new AbortController() : null);
    if (!request) {
      select.resolve({ variant: null });
      emit(EVENTS.variantChange, this.changeDetail(), this);
      if (spoken) this.announceVariant();
      return this.flushQueue();
    }
    try {
      const doc = await fetchSection(this.dataset.variantSection, {
        url: this.dataset.productUrl,
        params: { variant: variant.id },
        signal: request.signal,
        // Po zmianie koszyka odpowiedzi z pamięci miałyby nieaktualną ilość w koszyku.
        cache: !this.cartChanged,
      });
      if (request !== this.request) return;
      swapFragments(doc, this.root);
      this.afterSwap();
      const view = readJson(doc.querySelector('[data-view-event]'), {});
      select.resolve({ variant: view.product?.selectedVariant ?? null, detail: { html: doc, productId: this.dataset.productId, resource: variant } });
      emit(EVENTS.variantChange, this.changeDetail(), this);
    } catch (error) {
      if (request !== this.request) return;
      if (error.name !== 'AbortError') console.warn(error);
      select.reject(error);
    } finally {
      if (request === this.request) {
        this.request = null;
        if (spoken) this.announceVariant();
        this.flushQueue();
      }
    }
  }

  // Wariant, cena i dostępność (tekst ze świeżego fragmentu; w oknie bez bloku dostępności — stan przycisku).
  announceVariant() {
    const v = this.variant;
    const status = this.querySelector('[data-fragment="availability"] .availability__stock');
    const stock = status && [...status.children].map((el) => el.textContent.trim()).filter(Boolean).join(' ');
    const state = stock || (!v?.available && buttonLabel(v, strings(this.querySelector('[data-buy-buttons]'))));
    const parts = v ? [v.title, formatMoney(v.price), state] : [state];
    announce(parts.filter(Boolean).join(', ').replace(/\s+/g, ' ').trim());
  }

  // Kształt wariantu z ARCHITEKTURA §5.2 (ceny w groszach); null — kombinacja bez wariantu.
  changeDetail() {
    const v = this.variant;
    const variant = v && {
      id: v.id, price: v.price, compare_at_price: v.compare_at_price, available: v.available, inventory_quantity: v.inventory_quantity,
      inventory_policy: v.inventory_policy, sku: v.sku, title: v.title, featured_media_id: v.featured_media_id,
    };
    return { productId: this.dataset.productId, sectionId: this.dataset.sectionId, variant };
  }

  // shopify:product:select (PDP-CTR-03): obietnica rozstrzygana po odpowiedzi sekcji.
  selectEvent(selected) {
    const resolvers = {};
    const promise = new Promise((resolve, reject) => Object.assign(resolvers, { resolve, reject }));
    promise.catch(() => {});
    const { product } = readJson(this.querySelector('[data-view-event]'), {});
    if (product && selected.length) {
      standardEvent('shopify:product:select', { product: { id: String(product.id), title: product.title, handle: product.handle }, selectedOptions: selected, promise }, this);
    }
    return resolvers;
  }

  // Stan z JSON wariantu; klucz fragmentu czyścimy, żeby odpowiedź serwera zawsze go nadpisała.
  renderState() {
    const { variant } = this;
    this.available = Boolean(variant?.available);
    const state = this.querySelector('[data-fragment="buy-buttons-state"]');
    if (state) {
      state.dataset.fragmentKey = '';
      if (variant) state.querySelector('[data-variant-input]').value = variant.id;
      state.querySelector('[data-add-button]').disabled = !this.available;
      state.querySelector('[data-add-label]').textContent = buttonLabel(variant, strings(this.querySelector('[data-buy-buttons]')));
    }
    this.querySelector('[data-dynamic-checkout]')?.toggleAttribute('hidden', !this.available);
    const input = this.quantityInput;
    if (input) {
      if (variant) {
        const { min, max, increment } = variant.quantity_rule;
        input.min = min;
        input.step = increment;
        if (max) input.max = max;
        else input.removeAttribute('max');
        input.value = fitQuantity(input.value, this.limits());
      }
      input.disabled = !this.available;
    }
    this.syncQuantity();
    this.updateBar();
  }

  // Znaczniki jak snippet price (context pdp); po chwili zastępuje je fragment z serwera.
  renderPrice(variant) {
    const box = this.querySelector('[data-fragment="price"]');
    if (!box) return;
    const s = strings(this.querySelector('[data-variant-picker]'));
    const amount = (cents) => `${formatMoney(cents, { withCurrency: false })} <span class="price__currency">${Theme.currency?.symbol || 'zł'}</span>`;
    const compare = variant.compare_at_price;
    const sale = compare > variant.price;
    const discount = sale ? Math.round(((compare - variant.price) * 100) / compare) : 0;
    box.dataset.fragmentKey = '';
    box.innerHTML = `<div class="price price--pdp${sale ? ' price--sale' : ''}"><span class="price__amount">${sale ? `<span class="visually-hidden">${s.priceSale} </span>` : ''}${amount(variant.price)}</span>`
      + (sale ? ` <s class="price__compare"><span class="visually-hidden">${s.priceRegular} </span>${amount(compare)}</s>` : '')
      + (discount > 0 ? ` <span class="price__discount">&minus;${discount}%</span>` : '')
      + '</div>';
  }

  // PDP: ?variant= bez wpisu historii; w oknie szybkiego dodania — linki do pełnej strony.
  updateLinks(variant) {
    if (!this.pdp) {
      if (!variant) return;
      for (const link of this.querySelectorAll('[data-product-link]')) link.href = `${this.dataset.productUrl}?variant=${variant.id}`;
      return;
    }
    const url = new URL(location.href);
    if (variant) url.searchParams.set('variant', variant.id);
    else url.searchParams.delete('variant');
    history.replaceState(history.state, '', url);
  }

  afterSwap() {
    this.available = !this.querySelector('[data-add-button]')?.disabled;
    this.renderShipping();
    this.countSpecs();
    this.syncQuantity();
    this.updateBar();
  }

  // Licznik w zakładce „Dane techniczne” odświeżany z wierszami wariantu (BG-15).
  countSpecs() {
    const count = this.querySelector('[data-specs-count]');
    const table = this.querySelector('[data-specs-panel] [data-specs-table]');
    if (!count || !table) return;
    const rows = table.querySelectorAll('tr').length;
    count.textContent = rows;
    count.hidden = rows < 1;
  }

  // ----- Ilość i dodanie do koszyka (PDP-ATC-01…04, NT-15) -----

  cartCount() {
    return this.inCart.get(this.currentId()) || 0;
  }

  // Efektywne maksimum = maksimum reguły − ilość w koszyku, nie mniej niż minimum.
  limits() {
    const input = this.quantityInput;
    const min = Number(input?.min) || 1;
    const step = Number(input?.step) || 1;
    const max = input?.max ? Math.max(min, Number(input.max) - this.cartCount()) : Infinity;
    return { min, max, step };
  }

  syncQuantity() {
    const input = this.quantityInput;
    if (!input) return;
    const value = Number(input.value);
    const { min, max } = this.limits();
    const box = input.parentElement;
    const minus = box.querySelector('[name="minus"]');
    const plus = box.querySelector('[name="plus"]');
    if (minus) minus.disabled = input.disabled || !(value > min);
    if (plus) plus.disabled = input.disabled || !(value < max);
    const label = this.querySelector('[data-sticky-qty]');
    if (label) {
      label.hidden = !(value > 1);
      label.textContent = ` (${value})`;
    }
  }

  stepQuantity(direction) {
    const input = this.quantityInput;
    const limits = this.limits();
    input.value = fitQuantity(Number(input.value) + direction * limits.step, limits);
    this.syncQuantity();
  }

  // Po wyjściu z pola wartość wraca w granice; niezgodna z krokiem — natywny komunikat bez zmiany (FR-13-19).
  fixQuantity(input) {
    const limits = this.limits();
    const value = Number(input.value);
    if (!input.value || value < limits.min || value > limits.max) input.value = fitQuantity(value, limits);
    else if (!input.checkValidity()) input.reportValidity();
    this.syncQuantity();
  }

  addToCart(button) {
    const form = this.querySelector('form[data-product-form]');
    const id = Number(this.currentId());
    if (!form || !id || !this.available || this.adding) return;
    if (!form.checkValidity()) return void form.reportValidity();
    const quantity = Number(this.quantityInput?.value) || 1;
    const max = Number(this.quantityInput?.max) || Infinity;
    if (this.cartCount() + quantity > max) return this.showLimit(max);
    burst(button);
    // W trakcie zmiany wariantu pozycja czeka w kolejce na koniec odświeżenia (NT-15).
    this.queue.push({ id, quantity });
    this.queueButton = button;
    this.flushQueue();
  }

  async flushQueue() {
    if (!this.queue.length || this.adding || this.pending) return;
    const items = this.queue.splice(0);
    const button = [this.queueButton, this.querySelector('[data-add-button]')].find((el) => el?.isConnected);
    this.adding = true;
    button?.setAttribute('aria-busy', 'true');
    try {
      await cart.add(items, { source: this.pdp ? 'pdp' : 'quick-add', sourceEl: button });
      this.hideError();
      announce(Theme.strings?.added);
    } catch (error) {
      this.showError(error.message);
    } finally {
      this.adding = false;
      button?.removeAttribute('aria-busy');
    }
  }

  onCartChange(detail) {
    this.cartChanged = true;
    const items = detail?.cart?.items;
    if (!items) return;
    for (const id of this.inCart.keys()) {
      this.inCart.set(id, items.reduce((sum, item) => (String(item.variant_id) === id ? sum + item.quantity : sum), 0));
    }
    this.syncQuantity();
  }

  // Limit pozycji: przyciski wyłączone na 1 s, komunikat na 10 s (FR-13-21).
  showLimit(max) {
    const { maxLimit } = strings(this.querySelector('[data-buy-buttons]'));
    this.showError(fill(maxLimit, { max }));
    const buttons = [...this.querySelectorAll('[data-add-button], [data-sticky-add]')];
    for (const button of buttons) button.disabled = true;
    setTimeout(() => {
      for (const button of buttons) if (button.isConnected) button.disabled = !this.available;
    }, 1000);
  }

  showError(message) {
    if (!message) return;
    announce(message);
    const box = this.querySelector('[data-add-error]');
    if (!box) return;
    box.querySelector('[data-add-error-text]').textContent = message;
    box.hidden = false;
    clearTimeout(this.errorTimer);
    this.errorTimer = setTimeout(() => (box.hidden = true), 10000);
  }

  hideError() {
    clearTimeout(this.errorTimer);
    const box = this.querySelector('[data-add-error]');
    if (box) box.hidden = true;
  }

  // ----- Odliczanie wysyłki (PDP-AV-02) -----

  startShipping() {
    if (!this.querySelector('[data-fragment="availability"]')) return;
    this.renderShipping();
    this.shippingTimer = setInterval(() => this.renderShipping(), 20000);
  }

  renderShipping() {
    const box = this.querySelector('[data-fragment="availability"]');
    const ship = box?.querySelector('[data-shipping]');
    const delivery = box?.querySelector('[data-delivery]');
    if (!ship || !delivery) return;
    const text = shippingText(shippingPlan(new Date(), Number(Theme.settings?.cutoffHour ?? 12)), strings(box));
    if (ship.innerHTML !== text.ship) ship.innerHTML = text.ship;
    delivery.textContent = text.delivery;
  }

  // ----- Przyklejany stos kolumny zakupu ≥ 750 (PDP-STK-01…03, NT-28, FR-13-03) -----
  // Kolumna .is-stacked; bloki --pin: --pin-offset (od góry stosu) i --pin-reserve (dolny margines do końca treści
  // zatrzymanej kolumny), blok po nim --pin-return (górny margines, który go oddaje); kolumna --column-top (zatrzymanie).
  // Pierwszy blok stosu .is-stuck po przyklejeniu, ostatni .is-covering, gdy treść wjeżdża pod krawędź. Bez nasłuchu scroll.

  setupStack(signal) {
    this.column = this.querySelector('[data-buybox]');
    if (!this.column || !this.classList.contains('product-info--stack')) return;
    this.desktop = mq(750);
    const schedule = () => {
      cancelAnimationFrame(this.stackFrame);
      this.stackFrame = requestAnimationFrame(() => this.layoutStack());
    };
    this.desktop.addEventListener('change', schedule, { signal });
    // Sama wysokość okna nie zmienia rozmiaru kolumny, a od niej zależy miejsce zatrzymania.
    addEventListener('resize', debounce(schedule, 150), { signal });
    this.stackSizes = new ResizeObserver(schedule);
    this.stackSizes.observe(this.column);
    for (const pin of this.column.querySelectorAll(':scope > .product-info__block--pin')) this.stackSizes.observe(pin);
  }

  layoutStack() {
    const column = this.column;
    const blocks = [...column.children].filter((el) => el.classList.contains('product-info__block'));
    const pins = blocks.filter((el) => el.classList.contains('product-info__block--pin'));
    this.stuckObserver?.disconnect();
    this.coverObserver?.disconnect();
    for (const pin of pins) pin.classList.remove('is-stuck', 'is-covering');
    column.classList.remove('is-stacked');
    for (const block of blocks) for (const name of ['--pin-offset', '--pin-reserve', '--pin-return']) block.style.removeProperty(name);
    column.style.removeProperty('--column-top');
    // Strona ściśnięta szufladą koszyka: arkusz wyłącza przyklejenie (jedna kolumna).
    if (!this.desktop.matches || !pins.length || getComputedStyle(column).position !== 'sticky') return;

    // Naturalne położenia: przyklejenie przesuwa bloki, więc na czas jednego pomiaru są statyczne.
    for (const pin of pins) pin.style.position = 'static';
    const origin = column.getBoundingClientRect().top;
    const boxes = blocks.map((el) => {
      const rect = el.getBoundingClientRect();
      return { el, top: rect.top - origin, bottom: rect.bottom - origin, margin: parseFloat(getComputedStyle(el).marginBlockStart) || 0 };
    });
    for (const pin of pins) pin.style.position = '';
    pins[0].style.setProperty('--pin-offset', '0px');
    const stackTop = parseFloat(getComputedStyle(pins[0]).top);
    if (!Number.isFinite(stackTop)) return; // arkusz nie przykleja bloków

    // W stosie pod blokiem zostaje taki odstęp, jaki ma pod sobą w kolumnie (np. 8 px w nagłówku).
    const stack = boxes.filter((box) => pins.includes(box.el));
    let offset = 0;
    for (const box of stack) {
      const next = boxes[boxes.indexOf(box) + 1];
      box.offset = offset;
      offset += box.bottom - box.top + (next ? Math.max(0, next.top - box.bottom) : 0);
    }
    const last = stack.at(-1);
    // Krawędź stosu (linia) leży 16 px pod ostatnim blokiem.
    const edge = stackTop + last.offset + (last.bottom - last.top) + STACK_EDGE;
    const contentBottom = boxes.at(-1).bottom;

    // Kolumna staje, gdy ostatni boks jest 24 px nad dołem okna; gdy mieści się w oknie — po zebraniu stosu.
    let columnTop = Math.min(innerHeight - 24 - contentBottom, stackTop + last.offset - last.top);
    // Blok przecięty krawędzią stosu chowa się w całości (zatrzymanie przesunięte).
    const cut = boxes.find((box) => box.top > last.top && columnTop + box.top < edge && columnTop + box.bottom > edge);
    if (cut) columnTop = edge - cut.bottom;

    // Dolny margines do końca treści zatrzymanej kolumny: przy końcu siatki wszystkie bloki stosu ruszają
    // naraz z resztą kolumny, zamiast nachodzić na siebie; następny blok oddaje go górnym marginesem.
    const frozenBottom = columnTop + contentBottom;
    for (const box of stack) {
      const reserve = Math.max(0, frozenBottom - (stackTop + box.offset + (box.bottom - box.top)));
      box.el.style.setProperty('--pin-offset', `${box.offset}px`);
      box.el.style.setProperty('--pin-reserve', `${reserve}px`);
      const next = boxes[boxes.indexOf(box) + 1];
      next?.el.style.setProperty('--pin-return', `${next.margin - reserve}px`);
    }
    column.style.setProperty('--column-top', `${Math.round(columnTop)}px`);
    column.classList.add('is-stacked');

    const first = pins[0];
    this.stuckObserver = new IntersectionObserver(([entry]) => first.classList.toggle('is-stuck', entry.isIntersecting), { rootMargin: lineMargin(stackTop) });
    this.stuckObserver.observe(first);
    // Obszar od krawędzi stosu w dół: progi 0 i 1 łapią też skok przewinięcia (End, kotwica).
    const next = blocks[blocks.indexOf(last.el) + 1];
    if (!next) return;
    this.coverObserver = new IntersectionObserver(
      ([entry]) => last.el.classList.toggle('is-covering', entry.boundingClientRect.top < edge - 0.5),
      { rootMargin: `${-Math.round(edge)}px 0px 100000px 0px`, threshold: [0, 1] },
    );
    this.coverObserver.observe(next);
  }

  // ----- Pasek sticky ATC (PDP-SATC-01…04); ≥ 990 szeroki z kotwicami (sticky-atc--wide), stały -----

  setupStickyBar(signal) {
    const bar = (this.bar = this.querySelector('[data-sticky-atc]'));
    const buttons = this.querySelector('[data-buy-buttons]');
    if (!bar || !buttons) return;
    if (bar.classList.contains('sticky-atc--wide')) {
      this.wide = mq(990);
      this.wide.addEventListener('change', () => (this.updateBar(), this.syncReviews(), this.syncAnchors()), { signal });
      this.setupAnchors(signal);
    }
    // Pokazuje się, gdy przyciski zakupu są nad górną krawędzią okna; chowa przy stopce (margines 200 px).
    // Obszar sięga daleko w dół, więc skok przewinięcia nad okno i z powrotem (Home, kotwica) też zmienia stan.
    this.barObserver = new IntersectionObserver(([entry]) => {
      this.buttonsPassed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.updateBar();
    }, { rootMargin: '0px 0px 100000px 0px' });
    this.barObserver.observe(buttons);
    const footer = document.querySelector('#PageWrapper > footer') || document.querySelector('footer');
    if (footer) {
      this.footerObserver = new IntersectionObserver(([entry]) => {
        this.footerVisible = entry.isIntersecting;
        this.updateBar();
      }, { rootMargin: '200px 0px 0px 0px' });
      this.footerObserver.observe(footer);
    }
    // Wysokość paska dla toastu koszyka (KOS-TST-04) — zapis na samym pasku.
    this.barSize = new ResizeObserver(() => bar.style.setProperty('--sticky-atc-height', `${bar.offsetHeight}px`));
    this.barSize.observe(bar);
    customElements.whenDefined('shopify-chat').then(() => this.updateBar());
  }

  // Szeroki: także przy wariancie niedostępnym i obok czatu.
  updateBar() {
    if (!this.bar) return;
    const chat = Boolean(document.querySelector('shopify-chat'));
    const show = !this.footerVisible && (this.wide?.matches || Boolean(this.buttonsPassed && this.available && !chat));
    this.bar.classList.toggle('has-chat', chat);
    this.bar.classList.toggle('is-visible', show);
    this.bar.inert = !show;
  }

  // Kotwica widoczna z widocznym celem (zestaw i porównanie odsłania bundles.js); aktywna — cel na linii pod nagłówkiem.

  setupAnchors(signal) {
    this.anchorTargets = new Map();
    for (const link of this.bar.querySelectorAll('[data-anchor]')) {
      const target = document.querySelector(link.dataset.anchor || link.hash);
      if (!target) continue;
      target.id ||= link.hash.slice(1);
      link.hash = target.id;
      this.anchorTargets.set(link, target);
    }
    this.anchorWatch = new MutationObserver(() => this.syncAnchors());
    for (const target of this.anchorTargets.values()) this.anchorWatch.observe(target, { attributes: true, attributeFilter: ['hidden'] });
    addEventListener('resize', debounce(() => this.spy(), 200), { signal });
    this.syncAnchors();
  }

  syncAnchors() {
    if (!this.anchorTargets) return;
    for (const [link, target] of this.anchorTargets) link.hidden = !target.getClientRects().length;
    this.spy();
  }

  spy() {
    this.spyObserver?.disconnect();
    const shown = [...this.anchorTargets].filter(([link]) => !link.hidden);
    const hits = new Set();
    const mark = () => {
      const current = this.wide.matches && shown.find(([, target]) => hits.has(target))?.[0];
      for (const [link] of this.anchorTargets) link === current ? link.setAttribute('aria-current', 'location') : link.removeAttribute('aria-current');
    };
    if (!this.wide.matches || !shown.length) return mark();
    this.spyObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) hits[entry.isIntersecting ? 'add' : 'delete'](entry.target);
      mark();
    }, { rootMargin: lineMargin(headerStuck() + 24) });
    for (const [, target] of shown) this.spyObserver.observe(target);
  }

  // Cel 16 px pod nagłówkiem (jak :target w base.css).
  scrollToSection(target) {
    scrollTo({ top: scrollY + target.getBoundingClientRect().top - headerStuck() - 16, behavior: reducedMotion() ? 'instant' : 'smooth' });
    if (!target.hasAttribute('tabindex')) target.tabIndex = -1;
    target.focus({ preventScroll: true });
  }

  // ----- Zakładki (PDP-TAB-07, PDP-REV-01): przełączanie działa bez JS (radio + label) -----

  // „Wszystkie dane techniczne →” w opisie: przełącza zakładkę, a gdy pasek zakładek jest nad ekranem — pokazuje go.
  // ≥ 990 z kotwicami: przewija do danych technicznych.
  openSpecs(event) {
    event.preventDefault();
    const radio = this.querySelector('[data-specs-tab]');
    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
    if (this.wide?.matches) return this.scrollToSection(this.querySelector('[data-specs-panel]'));
    radio.focus({ preventScroll: true });
    const nav = this.querySelector('[data-tabs-nav]');
    const header = headerStuck();
    const top = nav?.getBoundingClientRect().top;
    if (top < header) scrollTo({ top: scrollY + top - header - 16, behavior: reducedMotion() ? 'instant' : 'smooth' });
  }

  setupReviews(signal) {
    this.reviewsTab = this.querySelector('[data-reviews-tab]');
    if (!this.reviewsTab) return;
    document.addEventListener('shopify:section:load', () => this.syncReviews(), { signal });
    this.syncReviews();
  }

  // Widżet Judge.me z sekcji „Opinie” trafia do panelu przy wyborze zakładki (≥ 990 z kotwicami — od razu). W edytorze
  // zostaje na miejscu (is-active), pusty panel ukryty; bez sekcji „Opinie” też.
  syncReviews() {
    const tab = this.reviewsTab;
    const panel = this.querySelector('[data-reviews-panel]');
    if (!tab || !panel) return;
    const show = tab.checked || Boolean(this.wide?.matches);
    const reviews = document.querySelector('[data-product-reviews]');
    panel.hidden = !reviews || Theme.designMode;
    if (Theme.designMode) {
      for (const el of document.querySelectorAll('[data-product-reviews]')) el.classList.toggle('is-active', show);
      return;
    }
    if (show && reviews && !panel.contains(reviews)) panel.append(reviews);
  }

  // ----- Zdarzenie widoku produktu (shopify:product:view) -----

  watchView() {
    const start = () => onIdle(() => {
      if (!this.controller) return;
      this.viewObserver = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        this.viewObserver.disconnect();
        this.sendView();
      });
      this.viewObserver.observe(this);
    }, 5000);
    if (document.readyState === 'complete') start();
    else addEventListener('load', start, { once: true, signal: this.controller.signal });
  }

  sendView() {
    const data = readJson(this.querySelector('[data-view-event]'));
    if (data) standardEvent('shopify:product:view', data, this);
  }
}

if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);
